import React, { useEffect, useRef, useState } from 'react';
import useTokenWallet from '../hooks/useTokenWallet';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Redeem Code Panel — $5 Tokenized Access Model
 * ---------------------------------------------
 * Per Architect directive (2026-05-21) and DIR-073 (Spark and Lens
 * Covenant): cost is not the gate. A student who purchases a $5 access
 * token (or is gifted one) can paste it here to unlock the same course
 * that costs more on the standard Gumroad rail.
 *
 * On success we persist the unlock in localStorage so the same browser
 * can resume access without re-entering the code.
 */
export default function RedeemCodePanel({ courseId }) {
  const [code, setCode] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | ok | err
  const [message, setMessage] = useState('');
  const [balance, setBalance] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const wallet = useTokenWallet();
  const sectionRef = useRef(null);
  const inputRef = useRef(null);

  // Architect directive (2026-05-31): the Course CTA enroll button
  // fires a global `scholar:open-redeem` event. We open the panel,
  // scroll it into view, and focus the code input so the student can
  // paste their token immediately.
  useEffect(() => {
    const onOpen = () => {
      setExpanded(true);
      requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setTimeout(() => inputRef.current?.focus(), 350);
      });
    };
    window.addEventListener('scholar:open-redeem', onOpen);
    return () => window.removeEventListener('scholar:open-redeem', onOpen);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setStatus('loading');
    setMessage('');
    try {
      const resp = await fetch(`${API_URL}/api/redeem/code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: code.trim(),
          course_id: courseId,
          email: email.trim() || null,
        }),
      });
      const data = await resp.json();
      if (resp.ok && data.redeemed) {
        setStatus('ok');
        setMessage(data.message || 'Access granted.');
        setBalance({
          remaining: data.tokens_remaining,
          spent: data.course_tokens,
          alreadyHad: Boolean(data.already_redeemed),
        });
        // Persist this code in the visitor's Federation token wallet so
        // the system-wide widget reflects the new balance immediately.
        wallet.upsert({
          code: code.trim().toUpperCase(),
          value: (data.tokens_remaining || 0) + (data.course_tokens || 0),
          balance: data.tokens_remaining || 0,
          source: 'gumroad',
        });
        try {
          const unlocked = JSON.parse(localStorage.getItem('scholar_unlocked') || '{}');
          unlocked[courseId] = { redeemed_at: data.redeemed_at, code: code.trim() };
          localStorage.setItem('scholar_unlocked', JSON.stringify(unlocked));
        } catch {
          /* localStorage disabled — non-fatal */
        }
      } else {
        setStatus('err');
        setMessage(data.detail || 'That code could not be redeemed.');
        setBalance(null);
      }
    } catch {
      setStatus('err');
      setMessage('Connection error. Try again in a moment.');
    }
  };

  return (
    <section
      ref={sectionRef}
      id="redeem-code-panel"
      data-testid="redeem-code-panel"
      className="border-y border-cyan-500/20 bg-gradient-to-b from-black to-gray-950 py-10"
    >
      <div className="container mx-auto px-4 max-w-3xl">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="redeem-code-toggle"
          aria-expanded={expanded}
          className="w-full flex items-center justify-between gap-4 text-left group"
        >
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-cyan-400/80">
              Federation Tokens · 1 Token = $5 · Spends on Books, Courses, and more
            </div>
            <h3 className="text-xl md:text-2xl font-semibold text-white mt-1 group-hover:text-amber-300 transition-colors">
              Have a Redeem Code?
            </h3>
            <p className="text-sm text-gray-400 mt-1">
              Federation Credits unlock <strong className="text-cyan-300">any course or book</strong> at
              its token price. Paste your access code below — daily date-codes like{' '}
              <code className="text-amber-300/80">5-27-fat</code> or longer codes both work.
            </p>
          </div>
          <span className="shrink-0 text-amber-300 font-mono text-sm">
            {expanded ? '▼ Hide' : '▲ Redeem'}
          </span>
        </button>

        {expanded && (
          <form onSubmit={submit} className="mt-6 grid gap-3" data-testid="redeem-code-form">
            <label className="text-xs uppercase tracking-wider text-gray-400" htmlFor="redeem-code-input">
              Access code
            </label>
            <input
              id="redeem-code-input"
              ref={inputRef}
              data-testid="redeem-code-input"
              type="text"
              autoComplete="off"
              spellCheck={false}
              placeholder="5-27-fat or SCHX-9YQT-5K2P"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={status === 'loading' || status === 'ok'}
              className="w-full bg-black border border-cyan-500/30 text-white font-mono tracking-widest px-4 py-3 rounded-lg uppercase placeholder:text-gray-600 focus:outline-none focus:border-amber-400 disabled:opacity-60"
            />

            <label className="text-xs uppercase tracking-wider text-gray-400 mt-2" htmlFor="redeem-code-email">
              Email (optional, so the Architect can follow up)
            </label>
            <input
              id="redeem-code-email"
              data-testid="redeem-code-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'loading' || status === 'ok'}
              className="w-full bg-black border border-cyan-500/20 text-white px-4 py-3 rounded-lg placeholder:text-gray-600 focus:outline-none focus:border-cyan-400 disabled:opacity-60"
            />

            <div className="flex items-center gap-3 mt-2">
              <button
                type="submit"
                data-testid="redeem-code-submit"
                disabled={status === 'loading' || status === 'ok' || !code.trim()}
                className="bg-amber-400 text-black font-bold px-6 py-3 rounded-lg hover:bg-amber-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'loading' ? 'Redeeming…' : status === 'ok' ? '✓ Access granted' : 'Redeem code'}
              </button>
              {status === 'ok' && (
                <span className="text-cyan-300 text-sm font-mono" data-testid="redeem-code-success">
                  {message}
                </span>
              )}
              {status === 'err' && (
                <span className="text-red-400 text-sm" data-testid="redeem-code-error">
                  {message}
                </span>
              )}
            </div>

            {status === 'ok' && balance && (
              <div
                data-testid="redeem-balance-block"
                className="mt-3 rounded-lg border border-cyan-500/30 bg-cyan-950/20 p-4"
              >
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="text-xs font-mono uppercase tracking-wider text-cyan-400/80">
                    Token Ledger
                  </span>
                  <span className="text-xs text-gray-500">·</span>
                  <span className="text-sm text-gray-300">
                    {balance.alreadyHad ? (
                      <>You already had access — <strong className="text-cyan-300">no tokens deducted</strong>.</>
                    ) : (
                      <>Spent <strong className="text-amber-300">{balance.spent} tokens</strong> on this course.</>
                    )}
                  </span>
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <span
                    className="text-2xl font-bold text-cyan-300"
                    data-testid="redeem-balance-remaining"
                    style={{ textShadow: '0 0 12px rgba(34, 211, 238, 0.4)' }}
                  >
                    {balance.remaining}
                  </span>
                  <span className="text-sm text-gray-400">tokens remaining on this code</span>
                </div>
                {balance.remaining > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    Paste this same code on another course to spend the rest.
                  </p>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-3 leading-relaxed">
              Per DIR-073 (Spark and Lens Covenant) — cost is not the gate. If you can't afford the
              full price and a token isn't available, write to{' '}
              <a href="mailto:keyhole@nextxus.net" className="text-cyan-400 hover:underline">
                keyhole@nextxus.net
              </a>{' '}
              and the Architect will issue you a code.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
